import React, { createContext, useContext, useMemo, useState } from 'react';

import { TId } from '@/features/common';

export const AddCandidateModalContext = createContext<{
  isAddCandidateModalOpen: boolean;
  isJobOpeningLocked: boolean;
  jobOpeningIdSelected: TId | null;
  stageIdSelected: TId | null;
  selectJobOpeningId: (jobOpeningId: TId) => void;
  selectStageId: (stageId: TId) => void;
  openAddCandidateModal: (jobOpeningId?: TId) => void;
  closeAddCandidateModal: () => void;
}>({
  isAddCandidateModalOpen: false,
  isJobOpeningLocked: false,
  jobOpeningIdSelected: null,
  stageIdSelected: null,
  selectJobOpeningId: () => {},
  selectStageId: () => {},
  openAddCandidateModal: () => {},
  closeAddCandidateModal: () => {},
});

export function AddCandidateModalContextProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isJobOpeningLocked, setJsJobOpeningLocked] = useState<boolean>(false);
  const [jobOpeningId, setJobOpeningId] = useState<TId | null>(null);
  const [stageId, setStageId] = useState<TId | null>(null);

  const contextProviderValue = useMemo(
    () => ({
      isAddCandidateModalOpen: isModalOpen,
      isJobOpeningLocked,
      jobOpeningIdSelected: jobOpeningId,
      stageIdSelected: stageId,
      selectJobOpeningId: (jobOpeningId: TId) => {
        setJobOpeningId(jobOpeningId);
        setStageId(null);
      },
      selectStageId: (stageId: TId) => setStageId(stageId),
      openAddCandidateModal: (jobOpeningId?: TId) => {
        setJobOpeningId(jobOpeningId ?? null);
        setJsJobOpeningLocked(!!jobOpeningId);
        setStageId(null);
        setIsModalOpen(true);
      },
      closeAddCandidateModal: () => {
        setIsModalOpen(false);
      },
    }),
    [isModalOpen, isJobOpeningLocked, jobOpeningId, stageId]
  );

  return (
    <AddCandidateModalContext.Provider value={contextProviderValue}>
      {children}
    </AddCandidateModalContext.Provider>
  );
}

export const useAddCandidateModal = () => {
  const context = useContext(AddCandidateModalContext);

  if (!context) {
    throw new Error(
      'useAddCandidateModal must be used within a AddCandidateModalContextProvider'
    );
  }

  return context;
};
